/*
 * word-to-image/ui.js — the /word-to-jpg page controller.
 *
 * The one design decision worth stating: the PREVIEW and the EXPORT come from
 * the SAME render. `open()` leaves the document laid out in a parked stage and
 * the thumbnails are rasterised from it; pressing Export rasterises the very
 * same DOM again at full resolution. A preview produced by a second,
 * independent pass is a preview that is allowed to disagree with the file the
 * user downloads — and it always eventually does.
 *
 * The second is that this page TELLS THE USER WHAT IT DID TO THEIR FONTS.
 * Every "word to jpg" service surveyed on 2026-08-26 substitutes silently, and
 * the top complaint about all of them is that the result does not look like the
 * document. Substitution is not the bug; not saying so is.
 *
 * The third (2026-08-26 redesign): ONE screen. Pages on the left, settings on
 * the right, the button under the settings, the result staying on the page.
 * Every control answers a question a person actually has — «which pages»,
 * «how big», «one file or many» — and nothing on the screen is a second way of
 * asking the same question.
 */

import * as WTI from './index.js';

const SLUG = 'word-to-jpg';
const T = window.__WTI_I18N || {};
const t = (k, fb) => (T[k] !== undefined ? T[k] : (fb !== undefined ? fb : k));

const $ = (id) => document.getElementById(id);

let session = null;
let thumbs = [];
let exportBlobs = null;
let busy = false;
let runToken = null;
let lastDelivery = 'none';   // single | zip | folder — how the file actually left
/* A long export on a phone is half a minute of work. Without this the only way
 * out is closing the tab — and the daily allowance would still have been spent.
 * rasterizePages checks the signal between PAGES, so the abort lands within one
 * page rather than at the end. */
let aborter = null;

/* ------------------------------------------------------------------ helpers */

function humanSize(n) {
    if (!Number.isFinite(n)) return '';
    if (n < 1024) return n + ' B';
    if (n < 1024 * 1024) return (n / 1024).toFixed(0) + ' KB';
    return (n / 1048576).toFixed(n < 10485760 ? 1 : 0) + ' MB';
}

/* Segmented controls are radio groups: the value is the checked radio. */
function radioValue(groupId, fallback) {
    const el = document.querySelector('#' + groupId + ' input:checked');
    return el ? el.value : fallback;
}
function setRadio(groupId, value) {
    const el = document.querySelector('#' + groupId + ' input[value="' + value + '"]');
    if (el && !el.disabled) { el.checked = true; return true; }
    return false;
}
const fmtValue = () => radioValue('wti-format', 'jpg');
const dpiValue = () => Number(radioValue('wti-dpi', '150')) || 150;

/* The output name is the user's material, cleaned by the shared primitive —
 * it knows the characters no filesystem takes, the BIDI marks that flip a name
 * in a file manager, and the Windows reserved names. The regex fallback knows
 * one of the three and exists only for a page where the script failed to load. */
function baseName() {
    const raw = ($('wti-out-name').value || '').trim();
    const ON = window.__outputName;
    let out = ON && ON.sanitize ? ON.sanitize(raw) : raw.replace(/[\\/:*?"<>|]+/g, '_');
    return (out && out.trim()) || 'document';
}

/* "1-3, 7, 9-end" → [1,2,3,7,9,...]. Empty means everything, which is what a
 * blank field should mean — not "nothing". */
function parseRange(text, total) {
    const s = String(text || '').trim().toLowerCase();
    if (!s || s === 'all' || s === '1-end') return null;      // null = all
    const out = new Set();
    for (const part of s.split(/[,;]+/)) {
        const chunk = part.trim();
        if (!chunk) continue;
        const m = chunk.match(/^(\d+)\s*[-–]\s*(\d+|end|последн\w*)$/);
        if (m) {
            const a = Math.max(1, parseInt(m[1], 10));
            const b = /^\d+$/.test(m[2]) ? parseInt(m[2], 10) : total;
            for (let i = Math.min(a, b); i <= Math.min(total, Math.max(a, b)); i++) out.add(i);
            continue;
        }
        const one = parseInt(chunk, 10);
        if (Number.isFinite(one) && one >= 1 && one <= total) out.add(one);
    }
    return out.size ? [...out].sort((a, b) => a - b) : [];
}

function setProgress(frac, label) {
    const wrap = $('wti-progress'), bar = $('wti-progress-bar'), txt = $('wti-progress-text');
    if (!wrap) return;
    if (frac === null) { wrap.classList.remove('is-on'); txt.textContent = label || ''; return; }
    wrap.classList.add('is-on');
    bar.style.width = Math.round(Math.max(0, Math.min(1, frac)) * 100) + '%';
    if (label !== undefined) txt.textContent = label;
}

function stage(name) { if (typeof window.__stage === 'function') window.__stage(name); }
function funnel(s, d) { if (typeof window.__aeFunnel === 'function') window.__aeFunnel(s, d || {}); }
function diag(d) { if (typeof window.__aeDiagnostic === 'function') window.__aeDiagnostic(d); }
function toast(msg, type) { if (window.__toast) window.__toast(msg, type); }

/* ── what this tool reports, and why each field is here ────────────────────
 *
 * Not "events for the dashboard" — each field answers a question we would
 * otherwise have to guess at, and one of them would have caught a bug we only
 * found by hand:
 *
 *   ms_open / ms_page / engine  — a one-page CV took 0.46 s on desktop and
 *     20.5 s on an iPhone, and nothing on the page said so. Per-phase timing
 *     WITH the browser engine is the only way that shows up in production
 *     instead of in a QA session.
 *   fail codes — DOC_LEGACY_FORMAT counts the people who bring an old .doc.
 *     If that number is large, the answer is a .doc reader, not better copy.
 *   subs / scripts / embedded — how often fidelity is at risk at all.
 *   pages_bucket, not pages — a raw page count is a fingerprint of the
 *     document; a bucket answers the same product question without one.
 *   picked_subset / stitch / dpi / format / delivery — which knobs are worth
 *     keeping, and how the file actually left (zip, folder, share sheet).
 *
 * Buckets rather than raw numbers wherever the raw number describes the user's
 * file rather than our behaviour. */
function bucketPages(n) {
    if (n <= 1) return '1';
    if (n <= 3) return '2-3';
    if (n <= 10) return '4-10';
    if (n <= 30) return '11-30';
    if (n <= 100) return '31-100';
    return '100+';
}
function bucketMs(ms) {
    if (ms < 500) return '<0.5s';
    if (ms < 2000) return '0.5-2s';
    if (ms < 5000) return '2-5s';
    if (ms < 15000) return '5-15s';
    if (ms < 45000) return '15-45s';
    return '45s+';
}
/* The engine, coarsely. A regression that only bites Safari is invisible in a
 * number averaged over Chrome. */
function engineTag() {
    const ua = navigator.userAgent || '';
    if (/CriOS|Chrome|Chromium|Edg/.test(ua)) return 'blink';
    if (/Firefox|FxiOS/.test(ua)) return 'gecko';
    if (/Safari/.test(ua)) return 'webkit';
    return 'other';
}

/* -------------------------------------------------------------- the report */

/* Two layers. The CHIPS say what a person needs before pressing the button:
 * how many pages, what paper, and whether the fonts are exact, substituted or
 * at risk. The ROWS underneath (folded) say which font became what — for the
 * person who wants to know, without the rest paying for it in screen space.
 * Everything here is measured from the file, and where a number cannot be
 * trusted the copy says so rather than dropping it. */
function renderReport(report) {
    const head = $('wti-report'), box = $('wti-report-details');
    if (!head || !box) return;

    const paper = report.pageW && report.pageH
        ? Math.round(report.pageW / 96 * 25.4) + '×' + Math.round(report.pageH / 96 * 25.4) + ' ' + t('mm', 'мм')
        : '';
    const chips = [];
    chips.push({ html: '<b>' + t('pages_n', ':n стр.').replace(':n', report.pages) + '</b>' });
    if (paper) chips.push({ text: paper + (report.landscape ? ' · ' + t('landscape', 'альбомная') : '') });

    /* Say it FIRST and say it plainly: this is not the author's page any more.
       An old .doc cannot be laid out in a browser, so what the reader is looking
       at is their text on a fresh page — useful, but not a photograph of the
       original. Burying that would be the quiet downgrade the whole report
       exists to prevent. */
    if (report.converted === 'legacy-doc') chips.push({ text: t('fact_legacy', 'Старый .doc: текст без разметки'), warn: true, expand: true });

    /* Word's own count, shown ONLY when it disagrees — and shown as a
     * disagreement, not as a correction. It is metadata, and metadata lies:
     * one file in the corpus claims three pages for a plainly six-page
     * document because a converter wrote the field. */
    if (report.wordPages && report.wordPages !== report.pages) {
        chips.push({ text: t('fact_word_pages', 'В Word: :n стр.').replace(':n', report.wordPages), warn: true, expand: true });
    }

    const f = report.fonts;
    const risk = f.risky.length || f.unmapped.length || (report.scripts && report.scripts.length);
    if (risk) chips.push({ text: t('fonts_risk', 'Шрифты: возможны отличия'), warn: true, expand: true });
    else if (f.substituted.length) chips.push({ text: t('fonts_subs', 'Шрифты заменены (:n)').replace(':n', f.substituted.length), expand: true });
    else if (report.embeddedFonts) chips.push({ text: t('fonts_embedded', 'Шрифты из файла'), good: true, expand: true });
    else chips.push({ text: t('fonts_exact', 'Шрифты: точные'), good: true });

    /* The rows. */
    const rows = [];
    if (report.converted === 'legacy-doc') {
        rows.push({ k: t('report_converted', 'Старый формат'), v: t('report_converted_doc'), warn: true });
    }
    if (report.wordPages && report.wordPages !== report.pages) {
        rows.push({ k: t('report_word_claim', 'В файле записано'), v: t('report_word_claim_v').replace(':n', report.wordPages), warn: true });
    }
    if (report.embeddedFonts) {
        rows.push({ k: t('report_embedded', 'Шрифты из файла'), v: String(report.embeddedFonts), good: true });
    }
    if (f.substituted.length) {
        rows.push({ k: t('report_substituted', 'Заменены'),
            v: f.substituted.map(x => x.font + ' → ' + x.with).join(', ') + ' · ' + t('report_metric_ok') });
    }
    if (f.risky.length) {
        rows.push({ k: t('report_risky', 'Внимание'), v: t('report_risky_v').replace(':fonts', f.risky.map(x => x.font).join(', ')), warn: true });
    }
    if (report.scripts && report.scripts.length) {
        rows.push({ k: t('report_scripts', 'Языки'),
            v: report.scripts.map(s => t('script_' + s, s)).join(', ') + ' · ' + t('report_scripts_v'), warn: true });
    }
    if (f.unmapped.length) {
        rows.push({ k: t('report_unmapped', 'Нет у нас'), v: f.unmapped.join(', ') + ' · ' + t('report_unmapped_v'), warn: true });
    }

    head.innerHTML = '';
    const hasRows = rows.length > 0;
    chips.forEach(c => {
        const expandable = hasRows && c.expand;
        const el = document.createElement(expandable ? 'button' : 'span');
        el.className = 'wti-fact' + (c.warn ? ' is-warn' : (c.good ? ' is-good' : '')) + (expandable ? ' wti-fact-btn' : '');
        if (expandable) {
            el.type = 'button';
            el.setAttribute('aria-expanded', 'false');
            el.setAttribute('aria-controls', 'wti-report-details');
            el.setAttribute('data-tap-skip', '');
            el.title = t('fact_details', 'Подробнее');
        }
        if (c.html) el.innerHTML = c.html; else el.textContent = c.text;
        head.appendChild(el);
    });

    box.innerHTML = rows.map(r =>
        '<div class="wti-rep-row' + (r.warn ? ' is-warn' : (r.good ? ' is-good' : '')) + '">' +
        '<span class="wti-rep-k"></span><span class="wti-rep-v"></span></div>').join('');
    [...box.children].forEach((el, i) => {
        el.querySelector('.wti-rep-k').textContent = rows[i].k;
        el.querySelector('.wti-rep-v').textContent = rows[i].v;
    });
    /* A warning opens the detail by itself: the person should not have to
       discover that the amber chip is a button to learn what is at risk. */
    const openNow = hasRows && (rows.some(r => r.warn));
    box.hidden = !openNow;
    head.querySelectorAll('.wti-fact-btn').forEach(b => b.setAttribute('aria-expanded', openNow ? 'true' : 'false'));
}

function toggleReportDetails() {
    const box = $('wti-report-details');
    if (!box || !box.children.length) return;
    box.hidden = !box.hidden;
    $('wti-report').querySelectorAll('.wti-fact-btn').forEach(b => b.setAttribute('aria-expanded', box.hidden ? 'false' : 'true'));
}

/* The resolution cards say how many pixels the page will be. Computed from the
 * document's own page size — «300 DPI» means nothing, «2480×3508» does. */
function updatePxLabels() {
    document.querySelectorAll('#wti-dpi em[data-px]').forEach(em => {
        if (!session) { em.textContent = ''; return; }
        const k = Number(em.dataset.px) / 96;
        const trim = $('wti-trim').checked;
        const r = session.report;
        const g = session.plan[0] && session.plan[0].geom;
        const w = trim && g ? g.pageW - g.padLeft - g.padRight : r.pageW;
        const h = trim && g ? g.contentH : r.pageH;
        em.textContent = Math.round(w * k) + '×' + Math.round(h * k) + ' px';
    });
}

/* ------------------------------------------------------------- refusals */

/* A refusal is a message that has to SURVIVE. It replaces the workspace, names
 * what the file actually is where we can tell, and says what to do next —
 * "не читается как .docx" is true and useless on its own. */
function showError(code) {
    const box = $('wti-error');
    if (!box) { toast(t('err_generic'), 'error'); return; }
    $('wti-workspace').hidden = true;
    const key = 'err_' + String(code).toLowerCase();
    const title = t(key, t('err_generic', 'Не удалось прочитать документ'));
    const why = t(key + '_why', t('err_generic_why', ''));
    $('wti-error-title').textContent = title;
    $('wti-error-why').textContent = why || '';
    $('wti-error-why').hidden = !why;
    box.hidden = false;
    // A toast as well: the panel is below the fold on a phone after a long page.
    toast(title, 'error');
    box.scrollIntoView({ block: 'nearest' });
}

function hideError() { const b = $('wti-error'); if (b) b.hidden = true; }

/* ------------------------------------------------------------- thumbnails */

/* ONE source of truth for "which pages". The thumbnails, the two chips and the
 * text field are all EDITORS of this set, never parallel opinions about it — a
 * checkbox grid and a range box that each believe they decide is how a tool
 * exports page 4 while showing page 3 selected. It also has to exist
 * independently of the thumbnails, because a 200-page document only ever
 * shows the first 60 and the tail must still be reachable. */
const selected = new Set();

function syncThumbChecks() {
    for (const cb of document.querySelectorAll('.wti-thumb-cb')) {
        cb.checked = selected.has(Number(cb.dataset.page));
    }
}

/* ⚠️ THUMBNAILS DO NOT GATE READINESS, and that is a measured decision.
 *
 * The first version rasterised every page at 36 dpi before showing anything.
 * Measured on iPhone 13 with a 20-page document (2026-08-26):
 *
 *     engine open, no thumbnails ....  1 526 ms
 *     thumbnails, all 20 pages ...... 23 143 ms   (1 157 ms per page)
 *
 * 94% of the wait was pictures nobody had asked for yet. The cost is per
 * html2canvas CALL — cloning the document — not per pixel, so a smaller
 * thumbnail does not help; only making fewer of them does.
 *
 * So: the workspace opens as soon as the pages are PLANNED, the grid appears
 * as numbered placeholders, and each preview is drawn when it scrolls into
 * view. A person converting page 1 of a 60-page contract now waits a second
 * and a half instead of a minute, and never pays for the other 59.
 *
 * ⚠️ ONE rasteriser at a time. `rasterizePages` MOVES the live section into a
 * capture frame; two passes over the same nodes would fight. Export sets
 * `thumbsStop` and the filler checks it between pages, so the two never
 * overlap. */
let thumbsStop = false;
let thumbObserver = null;

function buildThumbs() {
    const box = $('wti-thumbs');
    box.innerHTML = '';
    thumbs.forEach(URL.revokeObjectURL);
    thumbs = [];
    thumbsStop = false;
    if (thumbObserver) { thumbObserver.disconnect(); thumbObserver = null; }

    selected.clear();
    for (let i = 1; i <= session.plan.length; i++) selected.add(i);

    const cap = Math.min(60, session.plan.length);
    $('wti-thumbs-more').hidden = session.plan.length <= cap;
    if (session.plan.length > cap) {
        $('wti-thumbs-more').textContent =
            t('thumbs_more', 'Показаны первые :n страниц — остальные выбирайте полем ниже')
                .replace(':n', cap);
    }
    for (let i = 0; i < cap; i++) {
        const cell = document.createElement('label');
        cell.className = 'wti-thumb is-pending';
        cell.innerHTML =
            '<input type="checkbox" class="wti-thumb-cb" checked data-page="' + (i + 1) + '">' +
            '<span class="wti-thumb-box"></span>' +
            '<span class="wti-thumb-num">' + (i + 1) + '</span>' +
            '<button type="button" class="wti-thumb-zoom" data-zoom="' + (i + 1) + '" data-tap-skip ' +
            'title="' + t('zoom_open', 'Посмотреть крупно') + '" aria-label="' + t('zoom_open', 'Посмотреть крупно') + '">⤢</button>';
        box.appendChild(cell);
    }
    box.hidden = false;
    box.scrollLeft = 0;
    const two = session.plan.length >= 2;
    $('wti-select-all').hidden = !two;
    $('wti-only-first').hidden = !two;
    $('wti-range').hidden = !two;

    /* Draw what is on screen, and the rest as it arrives. `rootMargin` starts a
     * page slightly before it is visible so scrolling does not feel empty. The
     * observer sees clipping by the panel's own scroll box as well as by the
     * viewport, so the grid-with-inner-scroll and the phone strip both work. */
    const wanted = [];
    if ('IntersectionObserver' in window) {
        thumbObserver = new IntersectionObserver((entries) => {
            for (const e of entries) {
                if (!e.isIntersecting) continue;
                const n = Number(e.target.querySelector('.wti-thumb-cb').dataset.page);
                if (!wanted.includes(n)) { wanted.push(n); drainThumbs(wanted); }
                thumbObserver.unobserve(e.target);
            }
        }, { rootMargin: '300px 300px' });
        [...box.children].forEach(c => thumbObserver.observe(c));
    } else {
        for (let i = 1; i <= cap; i++) wanted.push(i);
        drainThumbs(wanted);
    }
}

let draining = false;
async function drainThumbs(queue) {
    if (draining) return;
    draining = true;
    try {
        while (queue.length && !thumbsStop && session) {
            const page = queue.shift();
            const cell = $('wti-thumbs').children[page - 1];
            if (!cell || !cell.classList.contains('is-pending')) continue;
            let img;
            try {
                [img] = await WTI.exportImages(session, {
                    format: 'jpg', dpi: 36, quality: 0.7, pages: [page],
                });
            } catch (_) { cell.classList.remove('is-pending'); continue; }
            if (!img) { cell.classList.remove('is-pending'); continue; }
            const url = URL.createObjectURL(img.blob);
            thumbs.push(url);
            const holder = cell.querySelector('.wti-thumb-box');
            if (holder) holder.style.backgroundImage = 'url("' + url + '")';
            cell.classList.remove('is-pending');
        }
    } finally { draining = false; }
}

/** Stop the preview filler and wait for the page it is on to finish. */
async function pauseThumbs() {
    thumbsStop = true;
    for (let i = 0; i < 200 && draining; i++) {
        await new Promise(r => setTimeout(r, 25));
    }
}
function resumeThumbs() {
    thumbsStop = false;
    const pending = [...document.querySelectorAll('.wti-thumb.is-pending .wti-thumb-cb')]
        .map(cb => Number(cb.dataset.page));
    if (pending.length) drainThumbs(pending);
}

/** null means "everything" — the engine treats that as the whole document. */
function selectedPages() {
    if (!session) return null;
    if (selected.size === session.plan.length) return null;
    return [...selected].sort((a, b) => a - b);
}

function syncSelectionUi() {
    if (!session) return;
    syncThumbChecks();
    const n = selected.size, total = session.plan.length;
    /* A disabled button with no reason next to it is a dead end. "999" and
     * "abc" both select nothing, and the user is left clicking a grey
     * rectangle — say what happened instead. */
    const typed = ($('wti-range').value || '').trim();
    $('wti-count').textContent = (n === 0 && typed)
        ? t('count_none', 'Под «:q» нет страниц — в документе их :n').replace(':q', typed).replace(':n', total)
        : t('count_of', ':n из :total').replace(':n', n).replace(':total', total);
    $('wti-select-all').classList.toggle('is-on', n === total);
    $('wti-only-first').classList.toggle('is-on', n === 1 && selected.has(1) && total > 1);
    $('wti-go').disabled = n === 0 || busy;
    updateStitchHint();
}

/* ─────────────────────────────────────────────────────── the page, readable
 *
 * A 104 px thumbnail answers "how many pages" and nothing else. The question a
 * person actually has in front of a 20-page contract is "which one has the
 * signature", and no grid of postage stamps answers it.
 *
 * The overlay is a real <dialog>: top layer, focus trap, Escape and the
 * backdrop for free. It rasterises THAT page from the same live document the
 * export uses — not a scaled-up thumbnail, which would be unreadable, and not
 * a second render, which would be allowed to disagree with the file they
 * download.
 *
 * ⚠️ One rasteriser at a time: the preview filler and the export borrow the
 * same live nodes, so opening the overlay pauses the filler and closing it
 * starts it again. */
let zoomPage = 0;
let zoomUrl = null;
let zoomBusy = false;
// One «did anyone open the magnifier» event per document — see zoomOpen().
let zoomReported = false;

function zoomOpen(page) {
    if (!session) return;
    const dlg = $('wti-zoom');
    if (typeof dlg.showModal === 'function') { if (!dlg.open) dlg.showModal(); }
    else dlg.setAttribute('open', '');
    document.documentElement.style.overflow = 'hidden';
    zoomShow(page);
    /* ONCE per document. `__ae` silently drops events once its queue reaches
       50, and the tail of a burst is where the completion event lives — a
       person paging through twenty pages must not be able to push it out.
       «Did anyone use the magnifier» needs one event, not twenty. */
    if (!zoomReported) { zoomReported = true; funnel('zoom_opened', { engine: engineTag() }); }
}

function zoomClose() {
    const dlg = $('wti-zoom');
    if (typeof dlg.close === 'function' && dlg.open) dlg.close();
    else { dlg.removeAttribute('open'); zoomAfterClose(); }
}

/* Runs on the dialog's `close` event — which is ALSO how Escape and the
 * light-dismiss arrive, so the cleanup happens on every way out, not only on
 * our own button. */
function zoomAfterClose() {
    document.documentElement.style.overflow = '';
    if (zoomUrl) { URL.revokeObjectURL(zoomUrl); zoomUrl = null; }
    $('wti-zoom-body').innerHTML = '';
    // Let the thumbnails finish filling now that the rasteriser is free.
    resumeThumbs();
}

async function zoomShow(page) {
    if (zoomBusy || !session) return;
    zoomPage = Math.max(1, Math.min(session.plan.length, page));
    $('wti-zoom-num').textContent = zoomPage + ' / ' + session.plan.length;
    $('wti-zoom-prev').disabled = zoomPage <= 1;
    $('wti-zoom-next').disabled = zoomPage >= session.plan.length;

    const body = $('wti-zoom-body');
    body.innerHTML = '';
    body.classList.add('is-busy');
    zoomBusy = true;
    try {
        await pauseThumbs();
        /* 110 dpi is chosen against the SCREEN, not the page: it makes an A4
         * about 900 px wide, which is legible on a laptop and still one quick
         * capture on a phone. The export resolution is a separate decision and
         * is not touched here. */
        const [img] = await WTI.exportImages(session, {
            format: 'jpg', dpi: 110, quality: 0.88, pages: [zoomPage],
        });
        if (zoomUrl) URL.revokeObjectURL(zoomUrl);
        zoomUrl = URL.createObjectURL(img.blob);
        const el = new Image();
        el.src = zoomUrl;
        el.alt = t('zoom_alt', 'Страница :n').replace(':n', zoomPage);
        body.innerHTML = '';
        body.appendChild(el);
    } catch (err) {
        body.textContent = t('err_export', 'Не удалось собрать картинки');
        try { console.error('[word-to-jpg] zoom', err); } catch (_) {}
    } finally {
        body.classList.remove('is-busy');
        zoomBusy = false;
    }
}

/* Copy the page as an image, so it can go straight into a chat without ever
 * becoming a file. PNG because that is the one image type every clipboard
 * implementation accepts; a JPEG blob is silently rejected by Safari.
 *
 * ⚠️ The ClipboardItem takes a PROMISE and write() is called from the click
 * itself. Safari (and Firefox) only honour a write that starts inside the user
 * gesture; `await export; then write()` arrives after the gesture has expired
 * and fails with NotAllowedError — for a whole browser, silently. */
function zoomCopy() {
    if (!session || zoomBusy) return;
    if (!navigator.clipboard || !window.ClipboardItem || !navigator.clipboard.write) {
        toast(t('copy_unsupported', 'Этот браузер не умеет копировать картинки'), 'warning');
        diag({ issue: 'copy_unsupported' });
        return;
    }
    const btn = $('wti-zoom-copy');
    btn.disabled = true;
    const pageNo = zoomPage;
    const png = (async () => {
        await pauseThumbs();
        const [img] = await WTI.exportImages(session, {
            format: 'png', dpi: dpiValue(), pages: [pageNo], trim: $('wti-trim').checked,
        });
        return img.blob;
    })();
    let item;
    try { item = new window.ClipboardItem({ 'image/png': png }); }
    catch (_) { item = null; }
    const write = item ? navigator.clipboard.write([item])
        : png.then(b => navigator.clipboard.write([new window.ClipboardItem({ 'image/png': b })]));
    write.then(() => {
        toast(t('copied', 'Страница скопирована'), 'success');
        funnel('page_copied', { engine: engineTag() });
    }).catch(err => {
        toast(t('copy_failed', 'Не удалось скопировать'), 'error');
        /* Clipboard writes fail for reasons the page cannot see (permission
           policy, a non-secure context, focus lost to another window). Without
           this the feature could be broken for a whole browser and look fine. */
        diag({ issue: 'copy_failed', why: (err && err.name) || 'unknown' });
        try { console.error('[word-to-jpg] copy', err); } catch (_) {}
    }).finally(() => { btn.disabled = false; });
}

/** Save just this page, at the chosen export settings. */
async function zoomSave() {
    if (!session || zoomBusy) return;
    const btn = $('wti-zoom-save');
    btn.disabled = true;
    try {
        await pauseThumbs();
        const format = fmtValue();
        const [img] = await WTI.exportImages(session, {
            format, dpi: dpiValue(), quality: Number($('wti-quality').value) / 100,
            trim: $('wti-trim').checked, pages: [zoomPage],
        });
        window.__downloadFile(img.blob, baseName() + '-p' + zoomPage + '.' + WTI.FORMATS[format].ext);
        funnel('page_saved_single', { engine: engineTag(), format });
    } catch (err) {
        toast(t('err_export', 'Не удалось собрать картинки'), 'error');
        try { console.error('[word-to-jpg] save one', err); } catch (_) {}
    } finally {
        btn.disabled = false;
    }
}

/* ------------------------------------------------------------ stitch limit */

/* The single tall image has a hard ceiling, and it is not the same on every
 * device: a canvas past the engine's area does not throw — it comes back
 * EMPTY. The first version discovered that after rendering all 20 pages at
 * 300 DPI, so the user waited twenty seconds to be told no. The arithmetic is
 * known before any work starts: page size × DPI × page count. So say it up
 * front, and say which resolution WOULD fit. The numbers come from the engine
 * (canvasLimits) so the prediction and the refusal can never disagree. */
function predictStitch(dpi) {
    if (!session || !selected.size) return null;
    const lim = WTI.canvasLimits();
    const k = (dpi || dpiValue()) / 96;
    const trim = $('wti-trim').checked;
    const g = session.plan[0] && session.plan[0].geom;
    const pw = trim && g ? g.pageW - g.padLeft - g.padRight : session.report.pageW;
    const ph = trim && g ? g.contentH : session.report.pageH;
    const w = Math.round(pw * k);
    const h = Math.round(ph * k) * selected.size;
    return { w, h, ok: w * h <= lim.area && h <= lim.side && w <= lim.side };
}

/** The highest offered DPI whose stitched image would actually assemble. */
function largestFittingDpi() {
    const offered = [...document.querySelectorAll('#wti-dpi input')].map(o => Number(o.value)).sort((a, b) => b - a);
    for (const d of offered) { const p = predictStitch(d); if (p && p.ok) return d; }
    return null;
}

function updateStitchHint() {
    const hint = $('wti-stitch-hint');
    if (!hint || !session) return;
    if (!$('wti-stitch').checked) { hint.textContent = t('stitch_hint'); hint.classList.remove('is-warn'); return; }
    const p = predictStitch();
    if (!p) { hint.textContent = t('stitch_hint'); hint.classList.remove('is-warn'); return; }
    if (p.ok) {
        hint.classList.remove('is-warn');
        hint.textContent = t('stitch_size', 'Получится одна картинка :w×:h пикселей.')
            .replace(':w', p.w).replace(':h', p.h);
    } else {
        hint.classList.add('is-warn');
        const fit = largestFittingDpi();
        hint.textContent = t('stitch_too_big', 'При этом разрешении вышло бы :w×:h — браузер столько не соберёт.')
            .replace(':w', p.w).replace(':h', p.h) +
            (fit ? ' ' + t('stitch_try_dpi', 'Поместится при :dpi DPI.').replace(':dpi', fit)
                 : ' ' + t('stitch_fewer', 'Выберите меньше страниц.'));
    }
}

/* ----------------------------------------------------------------- loading */

function clearResult() {
    const r = $('wti-result');
    if (r) r.hidden = true;
    $('wti-folder').hidden = true;
    pendingImages = null;
}

async function loadFile(file) {
    if (busy) return;
    busy = true;
    hideError();
    clearResult();
    stage('loading');
    $('wti-go').disabled = true;
    setProgress(0.05, t('reading', 'Читаем документ…'));
    try {
        if (session) { WTI.close(session); session = null; }
        thumbs.forEach(URL.revokeObjectURL);
        thumbs = [];
        exportBlobs = null;
        zoomReported = false;   // a new document is a new question

        const tOpen0 = Date.now();
        session = await WTI.open(file, {
            onProgress: (p) => setProgress(0.05 + p * 0.55, t('rendering', 'Раскладываем страницы…')),
        });
        /* Measured HERE, not after the thumbnails: this is the wait the user
         * feels before the document appears, and it is the number that would
         * have shown the 20-second iPhone stall in production. */
        const openMs = Date.now() - tOpen0;
        /* No second file header here: the shared uploader already collapses to
           one, with its own «Другой файл» and ×. Two rows naming the same file
           disagreed even about its size (4 MB against 3.7 MB — different
           rounding), which reads as two different files. */
        renderReport(session.report);
        buildThumbs();
        $('wti-out-name').value = file.name.replace(/\.[^.]+$/, '');
        $('wti-workspace').hidden = false;
        updatePxLabels();
        setProgress(null);
        syncSelectionUi();
        stage('ready');
        funnel('opened', {
            engine: engineTag(),
            /* Which lane the document came in on. Without it the legacy-.doc
               reader is unmeasurable: a converted file looks exactly like a
               .docx by the time this event fires. */
            src: session.report.converted || 'docx',
            ms_open: bucketMs(openMs),
            pages_bucket: bucketPages(session.report.pages),
            paper: session.report.landscape ? 'landscape' : 'portrait',
            mm: Math.round(session.report.pageW / 96 * 25.4) + 'x' + Math.round(session.report.pageH / 96 * 25.4),
            subs: session.report.fonts.substituted.length,
            unmapped: session.report.fonts.unmapped.length,
            scripts: (session.report.scripts || []).join('|') || 'latin',
            embedded: session.report.embeddedFonts,
            page_fields: session.report.pageFields ? 1 : 0,
            word_pages_differ: session.report.wordPages && session.report.wordPages !== session.report.pages ? 1 : 0,
        });
    } catch (err) {
        setProgress(null);
        const code = (err && err.code) || 'DOCX_UNREADABLE';
        showError(code);
        diag({ issue: 'open_failed', code, size: file && file.size });
        /* console only — NOT __handleError. That helper raises a SECOND toast
         * carrying the raw internals ("word-to-jpg/export: 2481x70180"), on top
         * of the translated one the user can act on. Two toasts, one of them
         * gibberish, is worse than one. */
        try { console.error('[word-to-jpg] open', code, err); } catch (_) {}
    } finally {
        busy = false;
        $('wti-go').disabled = !session;
    }
}

/* ------------------------------------------------------------------ export */

async function doExport() {
    if (!session || busy) return;
    busy = true;
    aborter = ('AbortController' in window) ? new AbortController() : { signal: {}, abort() {} };
    $('wti-go').disabled = true;
    $('wti-go').classList.add('is-busy');
    $('wti-cancel').hidden = false;
    clearResult();
    stage('exporting');
    // One rasteriser at a time — the preview filler borrows the same live nodes.
    await pauseThumbs();

    const unlock = () => { busy = false; $('wti-go').classList.remove('is-busy'); $('wti-go').disabled = false; $('wti-cancel').hidden = true; };

    /* The unit is the FILE, taken once per document — a page is not a
     * separate piece of work the user asked for. */
    if (window.__toolLimit) {
        runToken = window.__toolLimit.begin(SLUG, window.__toolLimit.fileKey(session.file));
        if (runToken === false) { unlock(); resumeThumbs(); return; }
    }

    const format = fmtValue();
    const dpi = dpiValue();
    const quality = Number($('wti-quality').value) / 100;
    const oneImage = $('wti-stitch').checked;
    const trim = $('wti-trim').checked;
    if (oneImage) {
        const pre = predictStitch(dpi);
        if (pre && !pre.ok) {
            /* Refuse before the work, not after it. */
            const fit = largestFittingDpi();
            toast(t('stitch_too_big', 'Картинка :w×:h — браузер столько не соберёт.')
                .replace(':w', pre.w).replace(':h', pre.h) +
                (fit ? ' ' + t('stitch_try_dpi', 'Поместится при :dpi DPI.').replace(':dpi', fit) : ''), 'error');
            diag({ issue: 'stitch_refused_upfront', w: pre.w, h: pre.h, dpi: dpi });
            if (runToken && runToken.abort) { runToken.abort(); runToken = null; }
            unlock(); resumeThumbs();
            return;
        }
    }
    const pages = selectedPages();
    const base = baseName();
    const count = pages ? pages.length : session.plan.length;

    const tExp0 = Date.now();
    funnel('export_started', {
        engine: engineTag(), format, dpi,
        stitch: oneImage ? 1 : 0,
        picked_subset: pages ? 1 : 0,
        pages_bucket: bucketPages(count),
    });
    try {
        setProgress(0, t('exporting', 'Рендерим страницы…'));
        let images = await WTI.exportImages(session, {
            format, dpi, quality, pages, trim, signal: aborter.signal,
            onProgress: (p) => setProgress(p * (oneImage ? 0.8 : 0.95),
                t('exporting_n', 'Страница :i из :n')
                    .replace(':i', Math.max(1, Math.round(p * count))).replace(':n', count)),
        });
        images.forEach(img => { img.name = img.name.replace(/^[^]*?(-p\d+)?(\.[a-z0-9]+)$/i, (m, p, e) => base + (p || '') + e); });

        if (oneImage && images.length > 1) {
            setProgress(0.85, t('stitching', 'Склеиваем в одну картинку…'));
            const one = await WTI.stitch(images, { format, quality, base });
            await deliverSingle(one.blob, one.name);
        } else if (images.length === 1) {
            await deliverSingle(images[0].blob, images[0].name);
        } else {
            await deliverMany(images, base);
        }

        /* begin() hands back a TOKEN OBJECT; commit/abort are its own methods, not
           functions on the module. Calling them on the module throws inside the
           catch handler and buries the real error. */
        if (runToken && runToken.commit) { runToken.commit(); runToken = null; }
        stage('done');
        const expMs = Date.now() - tExp0;
        if (typeof window.__aeToolComplete === 'function') {
            window.__aeToolComplete({
                engine: engineTag(), format, dpi,
                pages_bucket: bucketPages(images.length),
                ms_export: bucketMs(expMs),
                ms_page: bucketMs(Math.round(expMs / Math.max(1, images.length))),
                stitched: oneImage && images.length > 1 ? 1 : 0,
                trim: trim ? 1 : 0,
                src: (session && session.report.converted) || 'docx',
                delivery: lastDelivery,
                picked_subset: pages ? 1 : 0,
            });
        }
    } catch (err) {
        if (runToken && runToken.abort) { runToken.abort(); runToken = null; }
        setProgress(null);
        const code = (err && err.code) || 'EXPORT_FAILED';
        if (code === 'ABORTED') {
            /* The person changed their mind. Not an error, and the day's
             * allowance goes back — abort() above already returned it. */
            toast(t('cancelled', 'Отменено'), 'info');
            funnel('export_cancelled', { engine: engineTag(), dpi, format });
            return;
        }
        toast(t('err_' + code.toLowerCase(), t('err_export', 'Не удалось собрать картинки')), 'error');
        diag({ issue: 'export_failed', code, pages: session && session.plan.length });
        // console only — see the note in loadFile: __handleError adds a second
        // toast with the raw internals next to the one we just translated.
        try { console.error('[word-to-jpg] export', code, err); } catch (_) {}
    } finally {
        busy = false;
        aborter = null;
        $('wti-cancel').hidden = true;
        $('wti-go').classList.remove('is-busy');
        $('wti-go').disabled = false;
        syncSelectionUi();
        resumeThumbs();
    }
}

/* The result STAYS. A toast is gone in four seconds; the person who closed
 * the download bar, or who wants the same pages in a chat, needs a place to
 * press. «Поделиться» appears only where the share sheet can take files — on
 * a phone that is the direct road to Photos, Telegram and AirDrop, and it
 * side-steps the blob-download bugs iOS keeps regressing into. */
function showResult(text) {
    const box = $('wti-result');
    $('wti-result-txt').textContent = text;
    $('wti-again').hidden = !(exportBlobs && exportBlobs.length);
    $('wti-share').hidden = !canShareBlobs();
    box.hidden = false;
}

function shareFiles() {
    if (!exportBlobs || !exportBlobs.length) return [];
    try {
        return exportBlobs.map(b => new File([b.blob], b.name, { type: b.blob.type || 'application/octet-stream' }));
    } catch (_) { return []; }
}
function canShareBlobs() {
    if (!navigator.share || !navigator.canShare) return false;
    const files = shareFiles();
    if (!files.length) return false;
    try { return navigator.canShare({ files }); } catch (_) { return false; }
}
async function shareResult() {
    const files = shareFiles();
    if (!files.length) return;
    try {
        await navigator.share({ files, title: files[0].name });
        funnel('result_shared', { engine: engineTag(), n: files.length, delivery: lastDelivery });
    } catch (err) {
        // The person closed the sheet — not a failure to report.
        if (!/abort/i.test(String(err && err.name))) {
            toast(t('share_failed', 'Не удалось поделиться'), 'error');
            diag({ issue: 'share_failed', why: (err && err.name) || 'unknown' });
        }
    }
}
function downloadAgain() {
    if (!exportBlobs || !exportBlobs.length) return;
    for (const b of exportBlobs) window.__downloadFile(b.blob, b.name);
    funnel('download_again', { engine: engineTag(), delivery: lastDelivery });
}

async function deliverSingle(blob, name) {
    setProgress(1, t('saving', 'Сохраняем…'));
    window.__downloadFile(blob, name);
    exportBlobs = [{ blob, name }];
    lastDelivery = 'single';
    setProgress(null);
    showResult(t('result_single', 'Готово: :name (:size)').replace(':name', name).replace(':size', humanSize(blob.size)));
    toast(t('saved_one', 'Готово'), 'success');
}

/* Many pages.
 *
 * ⚠️ THE FOLDER PICKER CANNOT BE OPENED HERE. `showDirectoryPicker()` needs
 * live user activation, and the click that started the export spent it minutes
 * ago rendering pages — the call rejects with a SecurityError that surfaces as
 * "the export failed" for no reason the user can see. Measured exactly that way
 * on the real page. (Same class as the audio-gate rule in this codebase: a
 * privileged call cannot survive an await.)
 *
 * So: a ZIP by default, which needs no permission and is what people expect for
 * a handful of pages. The archive is refused ABOVE the measured live-blob
 * ceiling rather than built into nothing — past that wall the archive assembles,
 * the download reports success, and the disk stays empty. When that happens the
 * pages are KEPT and a folder button is offered, so the picker gets a fresh
 * click of its own. */
let pendingImages = null;

async function deliverMany(images, base) {
    const items = images.map(i => ({ name: i.name, blob: i.blob }));
    const total = items.reduce((a, i) => a + i.blob.size, 0);
    const BD = window.__batchDeliver;

    if (BD && BD.fitsArchive && !BD.fitsArchive(total)) {
        pendingImages = { items, base };
        setProgress(null);
        const btn = $('wti-folder');
        if (btn && BD.folderAvailable && BD.folderAvailable()) {
            btn.hidden = false;
            toast(t('need_folder', 'Слишком много данных для архива — выберите папку для сохранения'), 'warning');
            return;                       // not a failure: the user has a way out
        }
        toast(t('err_too_big_zip', 'Слишком много данных для архива — выберите меньше страниц'), 'error');
        throw Object.assign(new Error('ZIP_TOO_BIG'), { code: 'ZIP_TOO_BIG' });
    }

    setProgress(0.96, t('zipping', 'Упаковываем архив…'));
    if (!BD) throw Object.assign(new Error('BATCH_DELIVER_MISSING'), { code: 'SCRIPT_LOAD_FAILED' });
    const zip = await BD.packZip(items);
    const zipName = base + '.zip';
    window.__downloadFile(zip, zipName);
    exportBlobs = [{ blob: zip, name: zipName }];
    lastDelivery = 'zip';
    setProgress(null);
    showResult(t('result_zip', 'Архив :name (:size), файлов: :n')
        .replace(':name', zipName).replace(':size', humanSize(zip.size)).replace(':n', items.length));
    toast(t('saved_zip', 'Архив сохранён'), 'success');
}

/** The folder path, driven by ITS OWN click so the picker has activation. */
async function saveToFolder() {
    if (!pendingImages) return;
    const BD = window.__batchDeliver;
    const { items } = pendingImages;
    try {
        const sink = await BD.pickFolder();
        if (!sink) return;                // declined — nothing to report
        let done = 0;
        for (const it of items) {
            await sink.write(it.name, it.blob);
            setProgress(done / items.length, t('saving_n', 'Сохранено :i из :n')
                .replace(':i', ++done).replace(':n', items.length));
        }
        // close() resolves only once the writes are confirmed on disk.
        await sink.close();
        setProgress(null);
        pendingImages = null;
        exportBlobs = null;               // on disk already — nothing to re-download
        lastDelivery = 'folder';
        $('wti-folder').hidden = true;
        showResult(t('saved_folder', 'Сохранено в папку: :n').replace(':n', items.length));
        toast(t('saved_folder', 'Сохранено в папку: :n').replace(':n', items.length), 'success');
    } catch (err) {
        setProgress(null);
        // An AbortError here means the person closed the picker — not a failure
        // to report at all. Anything else gets one plain sentence, never the
        // raw exception text.
        if (!/abort/i.test(String(err && err.name))) toast(t('err_folder', 'Не удалось сохранить в папку'), 'error');
        try { console.error('[word-to-jpg] folder', err); } catch (_) {}
    }
}

/* -------------------------------------------------------------------- wire */

function init() {
    /* `__bindUploaderFile`, NOT a raw addEventListener.
     *
     * This module is loaded as a deferred ES module, so on a slow connection
     * the user can pick a file BEFORE this line runs — the uploader dispatches
     * `tool-uploader:file` to nobody and the tool sits dead until they pick
     * again. That is a real report, not a hypothetical (vocal-remover,
     * 2026-05-08). The helper attaches the listener AND drains the file the
     * uploader parked in `el.__pendingFile` while nothing was listening.
     * Guarded by UploaderListenerRaceTest. */
    const zone = $('wtiDrop');
    if (zone) {
        /* ⚠️ The helper calls back with (File, detail) — a FILE, not an Event.
         * The raw fallback below gets an Event. Two shapes, two handlers; one
         * handler trying to be both is how a tool silently receives `undefined`
         * on one of the paths. */
        if (typeof window.__bindUploaderFile === 'function') {
            window.__bindUploaderFile(zone, (file) => { if (file) loadFile(file); });
        } else {
            zone.addEventListener('tool-uploader:file', (e) => {
                const f = e && e.detail && e.detail.file;
                if (f) loadFile(f);
            });
        }
        zone.addEventListener('tool-uploader:clear', () => {
            if (session) { WTI.close(session); session = null; }
            thumbs.forEach(URL.revokeObjectURL); thumbs = [];
            exportBlobs = null;
            clearResult();
            $('wti-workspace').hidden = true;
            setProgress(null);
        });
    }

    /* Format. WebP is offered only where the canvas can actually encode it —
       Safari cannot, and silently hands back PNG under a .webp name. Disabling
       the card up front beats an error after the click. */
    const webpRadio = document.querySelector('#wti-format input[value="webp"]');
    if (webpRadio && WTI.canvasCanEncode && !WTI.canvasCanEncode('image/webp')) {
        webpRadio.disabled = true;
        webpRadio.parentElement.title = t('webp_unsupported', 'Этот браузер не кодирует WebP');
        webpRadio.parentElement.style.opacity = '.45';
    }
    const syncFormat = () => {
        const f = fmtValue();
        $('wti-quality-row').hidden = f === 'png';
        $('wti-out-ext').textContent = '.' + (WTI.FORMATS[f] ? WTI.FORMATS[f].ext : f);
    };
    $('wti-format').addEventListener('change', () => {
        syncFormat();
        if (window.__toolSettings) window.__toolSettings.save(SLUG, 'format', fmtValue());
    });
    $('wti-dpi').addEventListener('change', () => {
        updateStitchHint();
        if (window.__toolSettings) window.__toolSettings.save(SLUG, 'dpi', String(dpiValue()));
    });
    $('wti-quality').addEventListener('input', (e) => {
        $('wti-quality-val').textContent = e.target.value + '%';
    });

    /* The two switches show their trade-off only while ON. */
    const syncToggle = (id) => {
        const row = $(id + '-row');
        if (row) row.classList.toggle('is-on', $(id).checked);
    };
    $('wti-stitch').addEventListener('change', () => { syncToggle('wti-stitch'); updateStitchHint(); });
    $('wti-trim').addEventListener('change', () => {
        syncToggle('wti-trim');
        if (window.__toolSettings) window.__toolSettings.save(SLUG, 'trim', $('wti-trim').checked ? '1' : '0');
        updatePxLabels();
        updateStitchHint();
    });

    $('wti-report').addEventListener('click', (e) => {
        if (e.target.closest && e.target.closest('.wti-fact-btn')) toggleReportDetails();
    });

    $('wti-thumbs').addEventListener('click', (e) => {
        const z = e.target.closest && e.target.closest('.wti-thumb-zoom');
        if (!z) return;
        e.preventDefault();
        e.stopPropagation();
        zoomOpen(Number(z.dataset.zoom));
    });
    $('wti-thumbs').addEventListener('change', (e) => {
        const cb = e.target.closest && e.target.closest('.wti-thumb-cb');
        if (!cb) return;
        const n = Number(cb.dataset.page);
        if (cb.checked) selected.add(n); else selected.delete(n);
        $('wti-range').value = '';
        syncSelectionUi();
    });
    /* «Все» toggles: everything, or — when everything is already picked —
       nothing, so a person can start a hand-pick from a clean slate. */
    $('wti-select-all').addEventListener('click', () => {
        if (!session) return;
        const all = selected.size === session.plan.length;
        selected.clear();
        if (!all) for (let i = 1; i <= session.plan.length; i++) selected.add(i);
        $('wti-range').value = '';
        syncSelectionUi();
    });
    /* One tap for the case the forms actually ask for: the first page. */
    $('wti-only-first').addEventListener('click', () => {
        if (!session) return;
        selected.clear();
        selected.add(1);
        $('wti-range').value = '';
        syncSelectionUi();
    });
    /* The range field EDITS the set; it does not hold a second opinion about
     * it. Empty means "all", which is what an empty field should mean. */
    $('wti-range').addEventListener('input', (e) => {
        if (!session) return;
        const picked = parseRange(e.target.value, session.plan.length);
        selected.clear();
        if (picked === null) for (let i = 1; i <= session.plan.length; i++) selected.add(i);
        else picked.forEach(n => selected.add(n));
        syncSelectionUi();
    });

    /* The zoom dialog. `close` is the one exit every road passes through. */
    const dlg = $('wti-zoom');
    dlg.addEventListener('close', zoomAfterClose);
    dlg.addEventListener('cancel', (e) => { e.preventDefault(); zoomClose(); });
    $('wti-zoom-close').addEventListener('click', zoomClose);
    $('wti-zoom-prev').addEventListener('click', () => zoomShow(zoomPage - 1));
    $('wti-zoom-next').addEventListener('click', () => zoomShow(zoomPage + 1));
    $('wti-zoom-copy').addEventListener('click', zoomCopy);
    $('wti-zoom-save').addEventListener('click', zoomSave);
    // Light-dismiss for engines without `closedby`: a click on the dark area
    // (the dialog itself or its body, not the picture) closes.
    dlg.addEventListener('click', (e) => { if (e.target === dlg || e.target === $('wti-zoom-body')) zoomClose(); });
    document.addEventListener('keydown', (e) => {
        if (!dlg.open) return;
        if (e.key === 'ArrowLeft') zoomShow(zoomPage - 1);
        else if (e.key === 'ArrowRight') zoomShow(zoomPage + 1);
    });

    $('wti-go').addEventListener('click', doExport);
    $('wti-cancel').addEventListener('click', () => {
        if (aborter) { aborter.abort(); setProgress(null, t('cancelling', 'Отменяем…')); }
    });
    $('wti-again').addEventListener('click', downloadAgain);
    $('wti-share').addEventListener('click', shareResult);
    const retry = $('wti-error-retry');
    if (retry) retry.addEventListener('click', () => { hideError(); if (zone && zone.__uploaderOpen) zone.__uploaderOpen(); });
    $('wti-folder').addEventListener('click', saveToFolder);

    if (window.__toolSettings) {
        const S = window.__toolSettings;
        setRadio('wti-format', S.load(SLUG, 'format', 'jpg')) || setRadio('wti-format', 'jpg');
        setRadio('wti-dpi', S.load(SLUG, 'dpi', '150')) || setRadio('wti-dpi', '150');
        S.bind(SLUG, $('wti-quality'), 'quality', '92');
        if (S.load(SLUG, 'trim', '0') === '1') $('wti-trim').checked = true;
        $('wti-quality-val').textContent = $('wti-quality').value + '%';
    }
    syncFormat();
    syncToggle('wti-trim');
    syncToggle('wti-stitch');
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

/* Test seam: the Playwright suite drives the real page, not a mock. */
/* ⚠️ `ready` means READY FOR THE USER — workspace shown, previews built — not
 * "a session object exists". The first version reported the latter, and a
 * harness that waited on it raced ahead of the thumbnails and reported three
 * failures against working code. A state hook that answers a different question
 * than its name asks is a trap for whoever reads it next. */
window.__wtiState = () => ({
    ready: !!session && !!$('wti-workspace') && !$('wti-workspace').hidden,
    hasSession: !!session,
    report: session ? session.report : null,
    plan: session ? session.plan.map(p => ({ start: Math.round(p.start), end: Math.round(p.end) })) : null,
    thumbs: thumbs.length,
    format: fmtValue(),
    dpi: dpiValue(),
    selected: [...selected].sort((a, b) => a - b),
    lastExport: exportBlobs ? exportBlobs.map(b => ({ name: b.name, size: b.blob.size })) : null,
});
window.__wtiExportBlob = () => (exportBlobs && exportBlobs[0] ? exportBlobs[0].blob : null);
